import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Star, ShieldX, Check } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { STORY } from "../mock";
import { useStore } from "../context/StoreContext";
import { useSeo } from "../hooks/useSeo";

const Section = ({ eyebrow, title, action, children }) => (
  <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        {eyebrow && <p className="mb-1 font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#52B788]">{eyebrow}</p>}
        <h2 className="font-display text-3xl font-bold uppercase leading-none text-[#1B2a21] sm:text-4xl">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const Home = () => {
  const { sections: CATEGORIES, bestsellers: BESTSELLERS, newArrivals: NEW_ARRIVALS, saleItems: SALE_ITEMS } = useStore();
  useSeo({
    description: "Clean, high-strength supplements made in the UK. No fillers, no magnesium stearate. Shop magnesium, ashwagandha, turmeric & more for men, women and over 40s.",
    jsonLd: { "@context": "https://schema.org", "@type": "Organization", name: "British Supplements", url: window.location.origin, logo: `${window.location.origin}/logo512.png` },
  });
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1B4332]">
        <img
          src="https://images.unsplash.com/photo-1716751399795-20828ebfd3c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxib3RhbmljYWwlMjBncmVlbmVyeXxlbnwwfHx8Z3JlZW58MTc5MDkzMDM4OXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d281d] via-[#0d281d]/80 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 font-display text-xs font-semibold uppercase tracking-[0.25em] text-[#95D5B2]">
              <Leaf size={14} /> Clean · Nasty Free · UK Made
            </span>
            <h1 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] text-white sm:text-6xl lg:text-7xl">
              Supplements<br />made for <span className="text-[#74C69D]">people.</span><br />Not profit.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#cfe0d6]">
              Genuine, premium, high-strength supplements — with none of the fillers, stearates or hidden nasties Big Pharma pack into theirs.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/shop" className="group inline-flex items-center gap-2 rounded-full bg-[#52B788] px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#06160f] transition hover:bg-[#74C69D]">
                Shop All Products <ArrowRight size={18} className="transition group-hover:translate-x-1" />
              </Link>
              <button type="button" onClick={() => window.dispatchEvent(new Event("open-truth"))} data-testid="hero-read-truth-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white/10">
                Read The Truth
              </button>
              <Link to="/our-story" data-testid="hero-our-story-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-white/10">
                Our Story
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-[#cfe0d6]">
              <div className="flex text-[#FFD166]">{[0,1,2,3,4].map(i => <Star key={i} size={16} className="fill-current" />)}</div>
              <span className="text-sm">Rated excellent from <strong className="text-white">46,500+</strong> reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY TILES */}
      <Section eyebrow="Find Your Fit" title="Shop By You">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {CATEGORIES.map((c) => (
            <Link key={c.key} to={`/shop/${c.key}`} className="card-hover group relative aspect-square overflow-hidden rounded-2xl sm:aspect-[4/3]" data-testid={`category-tile-${c.key}`}>
              <img src={c.image} alt={c.label} className="h-full w-full object-cover transition duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06160f]/90 via-[#06160f]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-5">
                <h3 className="font-display text-lg font-bold uppercase leading-tight text-white sm:text-2xl">{c.label}</h3>
                <p className="mt-1 line-clamp-2 text-xs text-[#cfe0d6] sm:text-sm">{c.blurb}</p>
                <span className="mt-2 inline-flex items-center gap-1 font-display text-xs font-semibold uppercase tracking-wider text-[#95D5B2]">
                  Shop now <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* BESTSELLERS */}
      <div className="bg-[#d8ede0]">
        <Section
          eyebrow="Loved By Thousands"
          title="Bestsellers"
          action={<Link to="/shop" className="hidden items-center gap-1 font-display text-sm font-semibold uppercase tracking-wider text-[#2D6A4F] hover:text-[#1B4332] sm:inline-flex">View All <ArrowRight size={15} /></Link>}
        >
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {BESTSELLERS.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </Section>
      </div>

      {/* THEM VS US STRIP */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="overflow-hidden rounded-3xl border border-[#e6f0ea]">
          <div className="grid md:grid-cols-2">
            <div className="bg-[#fbf3f3] p-8 sm:p-10">
              <div className="mb-4 flex items-center gap-2 text-[#c1121f]"><ShieldX size={22} /><span className="font-display text-sm font-bold uppercase tracking-wider">Them (Big Pharma)</span></div>
              <ul className="space-y-3">
                {STORY.themVsUs.them.map((t, i) => (
                  <li key={i} className="flex gap-2 text-[15px] text-[#6b4a4a]"><span className="mt-1 text-[#c1121f]">✗</span>{t}</li>
                ))}
              </ul>
            </div>
            <div className="bg-[#1B4332] p-8 text-white sm:p-10">
              <div className="mb-4 flex items-center gap-2 text-[#95D5B2]"><Leaf size={22} /><span className="font-display text-sm font-bold uppercase tracking-wider">Us (The People)</span></div>
              <ul className="space-y-3">
                {STORY.themVsUs.us.map((t, i) => (
                  <li key={i} className="flex gap-2 text-[15px] text-[#dcebe2]"><Check size={18} className="mt-0.5 shrink-0 text-[#74C69D]" />{t}</li>
                ))}
              </ul>
              <Link to="/our-story" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#52B788] px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-[#06160f] transition hover:bg-[#74C69D]">
                See The Full Truth <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <div className="bg-[#eef7f1]">
      <Section eyebrow="Fresh In" title="New Arrivals">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {NEW_ARRIVALS.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </Section>
      </div>

      {/* SALE BANNER */}
      <section className="bg-[#eef7f1]">
        <div className="mx-auto max-w-7xl px-4 pb-14 sm:px-6">
        <div className="rounded-3xl bg-gradient-to-r from-[#2D6A4F] to-[#40916C] px-8 py-10 text-center sm:py-12">
          <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-[#B7E4C7]">No Pretend Sales</p>
          <h2 className="mt-2 font-display text-3xl font-bold uppercase text-white sm:text-4xl">Genuine Reductions, While Stocks Last</h2>
          <Link to="/shop/sale" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-[#1B4332] transition hover:bg-[#f0f7f2]">
            Shop The Sale <ArrowRight size={16} />
          </Link>
        </div>
        </div>
      </section>

      <div className="bg-[#d8ede0]">
      <Section eyebrow="Clean For Less" title="On Sale">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {SALE_ITEMS.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </Section>
      </div>
    </div>
  );
};

export default Home;
